// ================================================================
// vm-button
//
// Fully encapsulated button styles following the Vumedi Moderat
// design system. All visual variants are driven by host attributes
// (:host([attribute])) — no CSS class maps needed.
//
// Color architecture: primitive → semantic → component
//   $primitiveColors.brand.600  →  $colors.background.bg-brand-solid  →  get-color(background, bg-brand-solid)
// ================================================================

@use "Styles/mixins" as mixins;

// -- Host --------------------------------------------------------

:host {
    display: inline-flex;
    vertical-align: middle;
}

:host([hidden]) {
    display: none !important;
}

:host([full-width]) {
    display: flex;
    width: 100%;
}

// -- Interactive element base ------------------------------------

button,
a {
    // Reset
    appearance: none;
    background: none;
    border: 1px solid transparent;
    margin: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    text-decoration: none;
    cursor: pointer;

    // Layout
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;

    // Shape
    border-radius: 9999px;

    // Typography
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0;

    // Motion
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:focus {
        outline: none;
    }
}

// -- Content layout ----------------------------------------------

.content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.label {
    padding: 0 2px;
}

// -- Spinner -----------------------------------------------------

.spinner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: vm-btn-spin 0.75s linear infinite;
}

@keyframes vm-btn-spin {
    to {
        transform: rotate(360deg);
    }
}

// -- Slotted icon sizing -----------------------------------------

::slotted([slot="icon-leading"]),
::slotted([slot="icon-trailing"]) {
    display: flex;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
}

// ================================================================
// Sizes
// ================================================================

:host([size="sm"]) {
    button,
    a {
        height: 36px;
        padding: 8px 12px;
    }
}

:host([size="md"]) {
    button,
    a {
        height: 40px;
        padding: 8px 12px;
    }
}

:host([size="lg"]) {
    button,
    a {
        height: 44px;
        padding: 8px 12px;
    }
}

:host([size="xl"]) {
    button,
    a {
        height: 48px;
        padding: 8px 12px;
    }
}

// ================================================================
// Icon-only modifier
// ================================================================

:host([icon-only]) {
    .label {
        display: none;
    }

    button,
    a {
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        width: 20px;
        height: 20px;
    }
}

:host([icon-only][size="sm"]) {
    button,
    a {
        width: 36px;
    }
}
:host([icon-only][size="md"]) {
    button,
    a {
        width: 40px;
    }
}
:host([icon-only][size="lg"]) {
    button,
    a {
        width: 44px;
    }
}
:host([icon-only][size="xl"]) {
    button,
    a {
        width: 48px;
    }
}

// `xs` is icon-only — no standalone `:host([size="xs"])` height rule, so `size="xs"`
// without `icon-only` has no effect. The combined selector sets both dimensions and
// shrinks the icon to 12×12.
:host([icon-only][size="xs"]) {
    button,
    a {
        width: 24px;
        height: 24px;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        width: 12px;
        height: 12px;
    }
}

// ================================================================
// Hierarchy: Primary
// ================================================================

:host([variant="solid"][color-scheme="primary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-solid);
        color: mixins.get-color(text, text-white);
    }
}

:host([variant="solid"][color-scheme="primary"]:hover:not([disabled]):not([loading])),
:host([variant="solid"][color-scheme="primary"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-solid-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(background, bg-brand-subtle);
    }
}

// ================================================================
// Hierarchy: Secondary
// ================================================================

:host([variant="solid"][color-scheme="secondary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-subtle);
        color: mixins.get-color(text, text-brand-primary);
    }
}

:host([variant="solid"][color-scheme="secondary"]:hover:not([disabled]):not([loading])),
:host([variant="solid"][color-scheme="secondary"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-subtle-hover);
    }
}

// ================================================================
// Hierarchy: Tertiary
// ================================================================

:host([variant="outline"][color-scheme="neutral"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-white);
        color: mixins.get-color(text, text-secondary);
        border-color: mixins.get-color(border, border-primary);
    }
}

:host([variant="outline"][color-scheme="neutral"]:hover:not([disabled]):not([loading])),
:host([variant="outline"][color-scheme="neutral"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-primary-hover);
        color: mixins.get-color(text, text-secondary-hover);
    }
}

// ================================================================
// Hierarchy: Quaternary (ghost)
// ================================================================

:host([variant="ghost"][color-scheme="neutral"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-tertiary);
    }
}

:host([variant="ghost"][color-scheme="neutral"]:hover:not([disabled]):not([loading])),
:host([variant="ghost"][color-scheme="neutral"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-primary-hover);
        color: mixins.get-color(text, text-tertiary-hover);
    }
}

// ================================================================
// Hierarchy: Link color
// ================================================================

:host([variant="unstyled"][color-scheme="primary"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-brand-secondary);
        border-radius: 0;
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-brand-primary);
    }
}

:host([variant="unstyled"][color-scheme="primary"]:hover:not([disabled]):not([loading])) {
    button,
    a {
        color: mixins.get-color(text, text-brand-secondary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-brand-primary-hover);
    }
}

// ================================================================
// Hierarchy: Link gray
// ================================================================

:host([variant="unstyled"][color-scheme="neutral"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-tertiary);
        border-radius: 0;
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(text, text-tertiary);
    }
}

:host([variant="unstyled"][color-scheme="neutral"]:hover:not([disabled]):not([loading])) {
    button,
    a {
        color: mixins.get-color(text, text-tertiary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(text, text-tertiary-hover);
    }
}

// ================================================================
// Link variant size overrides
//
// Link hierarchies use the text line-height as their height
// instead of the padded button heights.
// ================================================================

:host([variant="unstyled"][color-scheme="primary"][size="sm"]),
:host([variant="unstyled"][color-scheme="neutral"][size="sm"]),
:host([variant="unstyled"][color-scheme="error"][size="sm"]),
:host([variant="unstyled"][color-scheme="primary"][size="md"]),
:host([variant="unstyled"][color-scheme="neutral"][size="md"]),
:host([variant="unstyled"][color-scheme="error"][size="md"]) {
    button,
    a {
        height: 20px;
    }
}

:host([variant="unstyled"][color-scheme="primary"][size="lg"]),
:host([variant="unstyled"][color-scheme="neutral"][size="lg"]),
:host([variant="unstyled"][color-scheme="error"][size="lg"]),
:host([variant="unstyled"][color-scheme="primary"][size="xl"]),
:host([variant="unstyled"][color-scheme="neutral"][size="xl"]),
:host([variant="unstyled"][color-scheme="error"][size="xl"]) {
    button,
    a {
        height: 24px;
    }
}

// ================================================================
// Error color-scheme
//
// Destructive combinations (color-scheme="error") layered onto each variant:
//   solid    → solid red   (Figma "Primary destructive")
//   outline  → red outline (Figma "Secondary destructive")
//   ghost    → red ghost   (Figma "Tertiary destructive")
//   unstyled → red link
// Structural styles (sizing, border, radius) come from the base rules above,
// so these only recolor. Disabled stays the standard gray (the disabled rules
// below come later in source order and win), so it isn't repeated here.
// ================================================================

// -- Primary error (solid red) --
:host([variant="solid"][color-scheme="error"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-solid);
        color: mixins.get-color(text, text-white);
    }
}

:host([variant="solid"][color-scheme="error"]:hover:not([disabled]):not([loading])),
:host([variant="solid"][color-scheme="error"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-solid-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(background, bg-error-primary-hover);
    }
}

// -- Tertiary error (red outline) --
:host([variant="outline"][color-scheme="error"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-white);
        color: mixins.get-color(text, text-error-primary);
        border-color: mixins.get-color(border, border-error-subtle);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-secondary);
    }
}

:host([variant="outline"][color-scheme="error"]:hover:not([disabled]):not([loading])),
:host([variant="outline"][color-scheme="error"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-primary);
        color: mixins.get-color(text, text-error-primary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-primary);
    }
}

// -- Quaternary error (red ghost) --
:host([variant="ghost"][color-scheme="error"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-error-primary);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-secondary);
    }
}

:host([variant="ghost"][color-scheme="error"]:hover:not([disabled]):not([loading])),
:host([variant="ghost"][color-scheme="error"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-primary);
        color: mixins.get-color(text, text-error-primary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-primary);
    }
}

// -- Link error (red link) -- mirrors the link-color layout (transparent bg,
// no padding, radius 0); only the palette differs. Size + focus-ring radius
// overrides are shared with the other link hierarchies further down.
:host([variant="unstyled"][color-scheme="error"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-error-primary);
        border-radius: 0;
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-secondary);
    }
}

:host([variant="unstyled"][color-scheme="error"]:hover:not([disabled]):not([loading])) {
    button,
    a {
        color: mixins.get-color(text, text-error-primary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-primary);
    }
}

// Loading reuses the hover background for Primary error (mirrors the brand
// Primary loading treatment).
:host([loading][variant="solid"][color-scheme="error"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-solid-hover);
        color: mixins.get-color(text, text-white);
    }
}

// ================================================================
// Disabled state
// ================================================================

:host([disabled]) {
    button {
        cursor: not-allowed;
    }

    a {
        cursor: not-allowed;
        pointer-events: none;
    }
}

:host([disabled][variant="solid"][color-scheme="primary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-disabled);
        color: mixins.get-color(foreground, fg-disabled);
        border-color: mixins.get-color(border, border-disabled-subtle);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled-subtle);
    }
}

:host([disabled][variant="solid"][color-scheme="secondary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-subtle);
        color: mixins.get-color(foreground, fg-disabled);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled);
    }
}

:host([disabled][variant="outline"][color-scheme="neutral"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-white);
        color: mixins.get-color(foreground, fg-disabled);
        border-color: mixins.get-color(border, border-disabled-subtle);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled-subtle);
    }
}

:host([disabled][variant="ghost"][color-scheme="neutral"]),
:host([disabled][variant="unstyled"][color-scheme="primary"]),
:host([disabled][variant="unstyled"][color-scheme="neutral"]),
:host([disabled][variant="unstyled"][color-scheme="error"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(foreground, fg-disabled);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled-subtle);
    }
}

// ================================================================
// Loading state
// ================================================================

:host([loading]) {
    button,
    a {
        pointer-events: none;
        cursor: wait;
    }

    .content {
        visibility: hidden;
    }
}

// Loading uses the hover background for Primary
:host([loading][variant="solid"][color-scheme="primary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-solid-hover);
        color: mixins.get-color(text, text-white);
    }
}

// ================================================================
// Focus ring
//
// Double box-shadow: inner white ring + outer brand ring.
// box-shadow is NOT clipped by overflow:hidden on the element.
// ================================================================

button:focus-visible,
a:focus-visible {
    box-shadow:
        0 0 0 2px mixins.get-color(background, bg-white),
        0 0 0 4px mixins.get-color(foreground, fg-brand-primary);
}

// Link variants use a smaller border-radius for the focus ring
:host([variant="unstyled"][color-scheme="primary"]),
:host([variant="unstyled"][color-scheme="neutral"]),
:host([variant="unstyled"][color-scheme="error"]) {
    button:focus-visible,
    a:focus-visible {
        border-radius: 4px;
    }
}

// Error hierarchies use the error focus ring (outer ring = error color).
:host([variant="solid"][color-scheme="error"]),
:host([variant="outline"][color-scheme="error"]),
:host([variant="ghost"][color-scheme="error"]),
:host([variant="unstyled"][color-scheme="error"]) {
    button:focus-visible,
    a:focus-visible {
        box-shadow:
            0 0 0 2px mixins.get-color(background, bg-white),
            0 0 0 4px mixins.get-color(foreground, fg-error-secondary);
    }
}
